import type { RefObject } from 'react'

import { useEffect, useRef, useState } from 'react'

interface InViewOptions {
    rootMargin?: string
    threshold?: number
    once?: boolean
}

export const useInView = <T extends Element>({
    rootMargin = '0px 0px -12% 0px',
    threshold = 0,
    once = true
}: InViewOptions = {}): { ref: RefObject<T | null>, inView: boolean } => {
    const ref = useRef<T>(null)
    const [inView, setInView] = useState(false)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        if (typeof IntersectionObserver === 'undefined') {
            setInView(true)
            return
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true)
                if (once) observer.disconnect()
            }
            else if (!once) {
                setInView(false)
            }
        }, { rootMargin, threshold })

        observer.observe(element)

        return () => observer.disconnect()
    }, [rootMargin, threshold, once])

    return { ref, inView }
}
